import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { MessageSquare, UserCog, Users, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { liveChatApi } from '../../../api/liveChat.api';
import { usersApi } from '../../../api';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      height: '100%',
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AgentAdminDashboardPage = ({ session = {} }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const getSessionAgentId = (sessionItem) =>
    sessionItem?.agent_id ||
    sessionItem?.assigned_agent_id ||
    sessionItem?.assigned_to ||
    sessionItem?.agent?.uid ||
    sessionItem?.assigned_agent?.uid ||
    sessionItem?.agent_uid ||
    '';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [sessionsResponse, usersResponse, assignmentsResponse] = await Promise.all([
          liveChatApi.getSessions(),
          usersApi.getUsers(),
          liveChatApi.getActiveAssignments(),
        ]);
        const sessionsPayload = sessionsResponse?.data?.data ?? sessionsResponse?.data ?? {};
        const sessionList = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions : [];
        const usersPayload = usersResponse?.data?.data ?? usersResponse?.data ?? [];
        const agentList = Array.isArray(usersPayload)
          ? usersPayload.filter((item) => String(item.u_role || item.role || '').toLowerCase() === 'agent')
          : [];
        const assignmentsPayload = assignmentsResponse?.data?.data ?? assignmentsResponse?.data ?? [];
        const assignmentList = Array.isArray(assignmentsPayload) ? assignmentsPayload : [];
        setSessions(sessionList);
        setAgents(agentList);
        setActiveAssignments(assignmentList);
      } catch (error) {
        setSessions([]);
        setAgents([]);
        setActiveAssignments([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const openSessions = useMemo(
    () => sessions.filter((item) => String(item.status || '').toLowerCase() === 'open'),
    [sessions]
  );
  const closedSessions = useMemo(
    () => sessions.filter((item) => String(item.status || '').toLowerCase() === 'closed'),
    [sessions]
  );
  const unassignedSessions = useMemo(
    () => openSessions.filter((item) => !getSessionAgentId(item)),
    [openSessions]
  );
  const busyAgentIds = new Set(
    activeAssignments
      .map((assignment) => assignment?.uid)
      .filter(Boolean)
      .map((id) => String(id))
  );
  const activeAgents = agents.filter(
    (agent) => agent.u_active === 1 && !busyAgentIds.has(String(agent.uid))
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1300, mx: 'auto' }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          p: { xs: 3, md: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #111827, #334155)',
          color: 'white',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          <Box>
            <Chip
              label="Agent Admin Console"
              icon={<UserCog size={14} />}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', mb: 2 }}
            />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Welcome, {session?.u_name || session?.name || 'Agent Admin'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              Allocate chats, monitor agents, and keep response times sharp.
            </Typography>
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/agent-admin/chat')}
              sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
            >
              Open Live Chat
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/agent-admin/agents')}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              Manage Agents
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <StatCard icon={MessageSquare} label="Open Chats" value={loading ? '-' : openSessions.length} color="#2563eb" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={Zap} label="Closed Chats" value={loading ? '-' : closedSessions.length} color="#f97316" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={Users} label="Agents" value={loading ? '-' : agents.length} color="#8b5cf6" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={UserCog} label="Active Agents" value={loading ? '-' : activeAgents.length} color="#10b981" />
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Allocation Queue
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sessions waiting for agent assignment.
              </Typography>
            </Box>
            <Chip label={`${unassignedSessions.length} waiting`} color="warning" variant="outlined" />
          </Stack>
          <Stack spacing={2}>
            {unassignedSessions.slice(0, 6).map((item) => (
              <Paper
                key={item.lcs_id}
                elevation={0}
                sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Session #{item.lcs_id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.cd_name || item.customer_name || 'Customer'} - {item.channel || 'whatsapp'}
                    </Typography>
                  </Box>
                  <Button size="small" variant="contained" onClick={() => navigate('/agent-admin/chat')}>
                    Assign Now
                  </Button>
                </Stack>
              </Paper>
            ))}
            {!loading && unassignedSessions.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  All chats are assigned. Great work!
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgentAdminDashboardPage;
